import { Drawer, Form, Rate, Input, Spin } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../../store/AuthContext/AuthContext';
import { authRequest } from '../../../../../../common/request/authRequest';
import { ButtonLZ } from '../../../../../components/design/Buttons/ButtonLZ';
import { LoadingOutlined } from '@ant-design/icons';
import { DrawerPanelTitle, drawerPanelStyles } from '../../../../common/Drawers/DrawerPanelTitle';

const stepNumberStyle = {
	color: '#86868b',
	fontWeight: 500 as const,
	fontSize: 12,
	textTransform: 'uppercase' as const,
	letterSpacing: '1.2px',
	display: 'block' as const,
	marginBottom: 4,
};

const stepTitleStyle = {
	color: '#1d1d1f',
	fontSize: 20,
	fontWeight: 600 as const,
	marginBottom: 16,
	marginTop: 0,
	lineHeight: 1.3,
};

export const TestReplyWithAIDrawer = (props: { openDrawer: boolean, setOpenDrawer: any, locationId?: any }) => {

	const [isLoading, setIsLoading] = useState(false);
	const [name, setName] = useState('');
	const [score, setScore] = useState(0);
	const [review, setReview] = useState('');
	const { t } = useTranslation();

	const [openTextArea, setOpenTextArea] = useState(false);
	const [replyForm] = Form.useForm();

	const { projectGroup } = useAuth();
	const locationIdPath = props.locationId ? '&locationId=' + projectGroup.group + '/locations/' + props.locationId : '';

	const hasScore = score > 0;
	const hasName = name.trim() !== '';
	const hasReview = review.trim() !== '';
	const isStep1Complete = hasScore && hasName && hasReview;
	const isButtonEnabled = isStep1Complete && !isLoading;

	const getResponseIa = async () => {
		if (!isButtonEnabled) return;
		setIsLoading(true);
		await authRequest(`${process.env.REACT_APP_API}/${projectGroup.group}/chat?reviewerName=${encodeURIComponent(name)}&starRating=${score}&content=${encodeURIComponent(review)}${locationIdPath}`, {})
			.then((data) => {
				handleOpenTextArea(data);
			})
			.catch(() => {})
			.finally(() => {
				setIsLoading(false);
			});
	}

	const handleOpenTextArea = (responseText: any) => {
		setOpenTextArea(true);
		replyForm.setFieldsValue({ comment: responseText });
	};

	const resetResult = () => {
		setOpenTextArea(false);
		replyForm.setFieldsValue({ comment: '' });
	};

	const modoPruebaBadge = (
		<div style={{
			display: 'inline-flex',
			alignItems: 'center',
			padding: '4px 10px',
			borderRadius: 8,
			background: '#e8e8ed',
			border: '1px solid #8e8e93',
			color: '#3a3a3c',
			fontWeight: 600,
			fontSize: 12,
			textTransform: 'uppercase',
			letterSpacing: '1.2px',
		}}>
			Modo Prueba
		</div>
	);

	return (
		<Drawer
			placement="right"
			width={'40%'}
			title={
				<DrawerPanelTitle
					title="Probar respuestas con IA"
					extra={modoPruebaBadge}
				/>
			}
			onClose={() => props.setOpenDrawer(false)}
			open={true}
			{...drawerPanelStyles}
		>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
				<div style={{ color: '#636366' }}>
					En esta pantalla podrás probar tu configuración del Agente de IA, probando diferentes textos y puntuaciones y validando las respuestas.
				</div>

				<section>
					<span style={stepNumberStyle}>Paso 01</span>
					<div style={stepTitleStyle}>Escribe tu nombre, texto y puntuación</div>

					<div style={{ width: '88%', margin: '0 auto' }}>
						<div
							style={{
								width: '100%',
								padding: '14px 16px',
								borderRadius: 10,
								background: '#e8f0f8',
								border: `1px solid ${isStep1Complete ? '#86efac' : '#c5d6e8'}`,
								overflow: 'hidden',
							}}
						>
							<div style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 10,
								marginBottom: 10,
							}}>
								<Input
									placeholder="Nombre usuario"
									value={name}
									onChange={(e) => {
										setName(e.target.value);
										resetResult();
									}}
									style={{
										flex: 1,
										background: '#ffffff',
										border: `1px solid ${hasName ? '#c5d6e8' : '#fdba74'}`,
										borderRadius: 8,
									}}
								/>
								<div style={{
									padding: '4px 8px',
									borderRadius: 8,
									background: hasScore ? '#ffffff' : '#fff7ed',
									border: `1px solid ${hasScore ? '#c5d6e8' : '#fdba74'}`,
									flexShrink: 0,
								}}>
									<Rate
										className="test-reply-rate"
										style={{ fontSize: 18, color: hasScore ? '#475569' : '#ea580c' }}
										value={score}
										onChange={(value) => {
											setScore(value);
											resetResult();
										}}
									/>
								</div>
							</div>

							<Input.TextArea
								className='input-field'
								rows={5}
								value={review}
								placeholder="Escribe aquí el texto de la reseña…"
								onChange={(e) => {
									setReview(e.target.value);
									resetResult();
								}}
								style={{
									width: '100%',
									background: '#ffffff',
									border: `1px solid ${hasReview ? '#c5d6e8' : '#fdba74'}`,
									borderRadius: 8,
								}}
							/>
						</div>
					</div>
				</section>

				<style>{`
					.test-reply-rate .ant-rate-star-first,
					.test-reply-rate .ant-rate-star-second {
						color: #d4d4d8;
					}
					.test-reply-rate .ant-rate-star-full .ant-rate-star-second,
					.test-reply-rate .ant-rate-star-half .ant-rate-star-first {
						color: inherit;
					}
				`}</style>

				<section>
					<span style={stepNumberStyle}>Paso 02</span>
					<div style={stepTitleStyle}>Responde con IA</div>

					{!isStep1Complete && (
						<div style={{
							fontSize: 12,
							color: '#94a3b8',
							marginBottom: 10,
						}}>
							Completa el nombre, la puntuación y el texto del Paso 01 para continuar
						</div>
					)}

					<div style={{ display: 'flex', justifyContent: 'flex-start' }}>
						<ButtonLZ
							onClick={getResponseIa}
							disabled={!isButtonEnabled}
							type='ghost'
							size='small'
							style={isButtonEnabled ? {
								fontSize: 12,
								border: '1px solid #e9d5ff',
								background: '#faf5ff',
								color: '#7e22ce',
								height: 30,
								padding: '0 10px',
							} : {
								fontSize: 12,
								border: '1px solid #d1d5db',
								background: '#f3f4f6',
								color: '#9ca3af',
								height: 30,
								padding: '0 10px',
								cursor: 'not-allowed',
								opacity: 1,
							}}
						>
							<span style={{
								display: 'flex',
								alignItems: 'center',
								gap: 5,
								fontWeight: 600,
								color: isButtonEnabled ? '#7e22ce' : '#9ca3af',
							}}>
								{isLoading
									? <Spin indicator={<LoadingOutlined spin style={{ fontSize: 14, color: '#7e22ce' }} />} />
									: <span
										className="material-symbols-outlined"
										style={{ fontSize: 15, color: isButtonEnabled ? '#7e22ce' : '#9ca3af' }}
									>
										auto_awesome
									</span>
								}
								{t('reviews.page.reply.review.btn.ia')}
							</span>
						</ButtonLZ>
					</div>

					{openTextArea && (
						<div style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 14,
							padding: '14px 16px 16px',
							borderRadius: 12,
							background: 'linear-gradient(180deg, #ede9fe 0%, #f5f3ff 38%, #ffffff 100%)',
							border: '1px solid #a78bfa',
							marginTop: 16,
						}}>
							<div style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								flexShrink: 0,
							}}>
								<span style={{
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: 28,
									height: 28,
									borderRadius: 8,
									background: '#7c3aed',
									color: '#ffffff',
									flexShrink: 0,
								}}>
									<span className="material-symbols-outlined" style={{ fontSize: 16, color: '#ffffff' }}>auto_awesome</span>
								</span>
								<div>
									<div style={{
										fontSize: 14,
										fontWeight: 700,
										color: '#0f172a',
										lineHeight: 1.3,
									}}>
										Respuesta generada con IA
									</div>
									<div style={{
										fontSize: 11,
										fontWeight: 500,
										color: '#94a3b8',
										lineHeight: 1.3,
										marginTop: 2,
									}}>
										Sugerencia a partir de la configuración del Agente de IA
									</div>
								</div>
							</div>

							<Form layout='vertical' form={replyForm} style={{ marginBottom: 0 }}>
								<Form.Item name='comment' style={{ marginBottom: 0 }}>
									<Input.TextArea
										className='input-field'
										rows={5}
										style={{
											width: '100%',
											background: '#ffffff',
											border: '1px solid rgba(148, 163, 184, 0.35)',
											borderRadius: 8,
										}}
									/>
								</Form.Item>
							</Form>
						</div>
					)}
				</section>
			</div>
		</Drawer>
	)
}
